import { useMemo, useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import {
  cadenceMs,
  formatWeight,
  freshness,
  type Measurement,
  type Point,
  type Sex,
  type UnitsPreference,
} from "@jackedlogs/shared";

import type { UiTheme } from "../../theme";
import { makeSettingsCardStyles } from "../settingsCardStyles";
import { ChipStrip, type ChipOption } from "./ChipStrip";
import { compositionSeries } from "./composition";
import { LineChart, type ChartSeries } from "../../charts/LineChart";
import { StaleBadge } from "../../primitives/StaleBadge";

// Measurement trends — the top of the Measurements page. A ChipStrip
// (multi-select) picks which metrics to chart; the default "Composition"
// chip shows weight + body-fat% + lean-mass as three trend lines, and each
// circumference with ≥2 data points gets its own selectable chip.
//
// Lifted to @jackedlogs/ui (S3, 2026-06-29): the chart is now the shared,
// interactive LineChart (neutral series + an orange "now" dot, tap-a-point
// tooltip, and a CURRENT value for EVERY series — the dashed Health-Connect
// curve finally shows its number, which was Antenore's main pain). theme +
// units + locale come in as props so the web console renders the same trends.
// Reduced-vision: one chart area, large type, ≥44pt chips, anti-clutter.

const COMPOSITION = "composition";

// Circumferences (head-to-toe) — weight + body-fat + lean live in the
// Composition group, height is effectively constant, so neither gets a
// standalone chip here.
const CIRC_FIELDS = [
  "neckCm",
  "chestCm",
  "waistCm",
  "hipsCm",
  "bicepsLCm",
  "bicepsRCm",
  "forearmsLCm",
  "forearmsRCm",
  "thighsLCm",
  "thighsRCm",
  "calvesLCm",
  "calvesRCm",
] as const;

type Series = {
  key: string;
  title: string;
  current: string;
  points: Point[];
  /** Measured curve imported via Health Connect, drawn dashed alongside the JL
   *  estimate (dual-curve, labelled by provenance — slice 2d). */
  secondaryPoints?: Point[];
  yFormat: (n: number) => string;
};

type Props = {
  items: Measurement[];
  sex: Sex | undefined;
  units: UnitsPreference;
  locale: string;
  theme: UiTheme;
  /** Clock, injected: the trends date a series that has gone quiet, and a
   *  render-time Date.now() would make that untestable (HM-1). */
  nowMs: number;
};

export function MeasurementTrends({ items, sex, units, locale, theme, nowMs }: Props) {
  const { t } = useTranslation();
  const card = useMemo(() => makeSettingsCardStyles(theme), [theme]);
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const [selected, setSelected] = useState<string[]>([COMPOSITION]);
  const [expanded, setExpanded] = useState(false);

  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        day: "2-digit",
        month: "short",
        year: "2-digit",
      }),
    [locale],
  );

  // Oldest→newest for charts; the list is newest-first from the API.
  const chrono = useMemo(() => [...items].reverse(), [items]);

  // All series, keyed; only those with ≥2 points are chartable.
  const series = useMemo(() => {
    const pt = (takenAt: string, y: number): Point => ({
      x: Date.parse(takenAt),
      y,
      label: dateFmt.format(new Date(takenAt)),
    });
    const last = (s: Point[]) => s[s.length - 1]?.y;
    const out: Record<string, Series> = {};
    const fmtKg = (n: number) => formatWeight(n, units, locale);
    // Lean mass is a BF%-derived estimate — show whole units, not the 3-dp
    // precision of a logged weight (false precision, inbox 65d1a6ed).
    const fmtKgWhole = (n: number) => formatWeight(n, units, locale, 0);
    const fmtCm = (n: number) => `${n} cm`;
    const fmtPct = (n: number) => `${n.toFixed(1)}%`;

    // Weight points stay in canonical kg; fmtKg (formatWeight) converts to
    // the user's unit for display — same as lean mass.
    const weight = chrono
      .filter((m) => m.weightKg !== undefined)
      .map((m) => pt(m.takenAt, m.weightKg as number));
    if (weight.length)
      out.weight = {
        key: "weight",
        title: t("mobile.body.weight", { defaultValue: "Weight" }),
        current: fmtKg(last(weight) as number),
        points: weight,
        yFormat: fmtKg,
      };

    // Body composition (the default, most-important trends). Navy BF% needs
    // height + circumferences (NOT weight); lean mass = weight × (1 − BF%)
    // moves on every weigh-in. compositionSeries carries each input forward
    // PER FIELD so a sparse tape day still recomputes BF% without freezing,
    // and never persists a derived value ([[feedback_no_denormalized_display_snapshots]]).
    const { fat: fatRaw, lean: leanRaw } = compositionSeries(chrono, sex);
    const fat = fatRaw.map((p) => pt(p.takenAt, p.value));
    const lean = leanRaw.map((p) => pt(p.takenAt, p.value));
    // Measured curves imported via Health Connect (slice 2d) — drawn dashed
    // alongside the Navy estimate, labelled by provenance.
    const hcFat = chrono
      .filter((m) => typeof m.bodyFatPct === "number")
      .map((m) => pt(m.takenAt, m.bodyFatPct as number));
    const hcLean = chrono
      .filter((m) => typeof m.leanMassKg === "number")
      .map((m) => pt(m.takenAt, m.leanMassKg as number));
    if (fat.length)
      out.fat = {
        key: "fat",
        title: t("mobile.body.metricBodyFat", { defaultValue: "Body fat" }),
        current: fmtPct(last(fat) as number),
        points: fat,
        secondaryPoints: hcFat.length ? hcFat : undefined,
        yFormat: fmtPct,
      };
    if (lean.length)
      out.lean = {
        key: "lean",
        title: t("mobile.body.metricLeanMass", { defaultValue: "Lean mass" }),
        current: fmtKgWhole(last(lean) as number),
        points: lean,
        secondaryPoints: hcLean.length ? hcLean : undefined,
        yFormat: fmtKgWhole,
      };

    for (const f of CIRC_FIELDS) {
      const pts = chrono
        .filter((m) => typeof m[f] === "number")
        .map((m) => pt(m.takenAt, m[f] as number));
      if (pts.length)
        out[f] = {
          key: f,
          title: t(`mobile.body.field_${f}`, { defaultValue: f }),
          current: fmtCm(last(pts) as number),
          points: pts,
          yFormat: fmtCm,
        };
    }
    return out;
  }, [chrono, sex, units, locale, t, dateFmt]);

  // Roster chips: Composition always, then any circumference with ≥2 points.
  const options = useMemo<ChipOption[]>(() => {
    const opts: ChipOption[] = [
      {
        key: COMPOSITION,
        label: t("mobile.body.metricComposition", {
          defaultValue: "Composition",
        }),
      },
    ];
    for (const f of CIRC_FIELDS) {
      const sf = series[f];
      if (sf && sf.points.length >= 2) opts.push({ key: f, label: sf.title });
    }
    return opts;
  }, [series, t]);

  // Selected keys → the concrete series to chart (Composition fans out to
  // weight + fat + lean), de-duped, ≥2 points only.
  const shown = useMemo(() => {
    const keys: string[] = [];
    for (const sel of selected) {
      if (sel === COMPOSITION) keys.push("weight", "fat", "lean");
      else keys.push(sel);
    }
    const seen = new Set<string>();
    const result: Series[] = [];
    for (const k of keys) {
      if (seen.has(k)) continue;
      seen.add(k);
      const s = series[k];
      if (s && s.points.length >= 2) result.push(s);
    }
    return result;
  }, [selected, series]);

  const toggle = (key: string) =>
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );

  return (
    <View style={card.card}>
      <ChipStrip
        label={t("mobile.body.trendsLabel", { defaultValue: "TRENDS" })}
        hint={t("mobile.body.trendsHint", { defaultValue: "select metrics" })}
        expanded={expanded}
        onToggleExpanded={() => setExpanded((v) => !v)}
        expandA11y={t("mobile.body.trendsExpandA11y", {
          defaultValue: "Show all metrics",
        })}
        collapseA11y={t("mobile.body.trendsCollapseA11y", {
          defaultValue: "Collapse metrics",
        })}
        theme={theme}
        options={options}
        mode="multi"
        selectedKeys={selected}
        onSelect={toggle}
      />

      {shown.length === 0 ? (
        <Text style={card.cardHelp}>
          {t("mobile.body.trendsEmpty", {
            defaultValue: "Log at least 2 measurements to see a trend.",
          })}
        </Text>
      ) : (
        shown.map((s) => {
          const chartSeries: ChartSeries[] = [
            { key: s.key, points: s.points, variant: "primary" },
          ];
          if (s.secondaryPoints)
            chartSeries.push({
              key: `${s.key}-hc`,
              points: s.secondaryPoints,
              variant: "secondary",
            });
          // The JL estimate (primary) is what the headline number reports, so
          // it is the series whose silence matters — a dashed Health-Connect
          // companion going quiet is a different, quieter story.
          const xs = s.points.map((p) => p.x);
          const fresh = freshness(xs, nowMs);
          const lastAt = xs[xs.length - 1];
          return (
            <View key={s.key} style={styles.chartBlock}>
              <View style={styles.chartHead}>
                <Text style={styles.chartTitle}>{s.title}</Text>
                <Text style={styles.chartCurrent}>{s.current}</Text>
              </View>
              {fresh.known && fresh.stale && lastAt !== undefined ? (
                <View style={styles.ageRow}>
                  <StaleBadge
                    theme={theme}
                    atMs={lastAt}
                    locale={locale}
                    cadenceMs={cadenceMs(xs)}
                  />
                </View>
              ) : null}
              <LineChart series={chartSeries} yFormat={s.yFormat} theme={theme} nowMs={nowMs} />
              {s.secondaryPoints ? (
                <Text style={styles.legend}>
                  {t("mobile.body.dualCurveLegend", {
                    defaultValue: "Solid: estimate · Dashed: Health Connect",
                  })}
                </Text>
              ) : null}
              {/* MS-2: body fat is a tape measurement, not a scale reading —
                  it moves with waist/neck (and hips for women), not weight. A
                  plain factual line, gender-aware from the sex already in scope. */}
              {s.key === "fat" ? (
                <Text style={styles.provenance}>
                  {sex === "female"
                    ? t("mobile.body.bodyFatProvenanceWithHips", {
                        defaultValue:
                          "Body fat is calculated from waist, neck and hips.",
                      })
                    : t("mobile.body.bodyFatProvenance", {
                        defaultValue: "Body fat is calculated from waist and neck.",
                      })}
                </Text>
              ) : null}
            </View>
          );
        })
      )}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    chartBlock: { gap: s.s1, marginTop: s.s3 },
    ageRow: { flexDirection: "row" },
    chartHead: {
      flexDirection: "row",
      alignItems: "baseline",
      justifyContent: "space-between",
    },
    chartTitle: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
      fontWeight: "700",
    },
    chartCurrent: {
      color: p.ink,
      fontSize: ty.sizes.md,
      fontWeight: "700",
      fontVariant: ["tabular-nums"],
    },
    legend: { color: p.ink3, fontSize: ty.sizes.xs, marginTop: 2 },
    // MS-2 provenance line: explanatory content (not a decorative legend), so a
    // step up from the xs legend to sm — readability beats density on text the
    // eye actually reads.
    provenance: { color: p.ink3, fontSize: ty.sizes.sm, marginTop: s.s1 },
  });
}
